'use client';

// Formules des animations : KaTeX avec les options imposées par le format
// (`JRAF_KATEX_OPTIONS` : `trust: false`, expansions et tailles bornées).
// Deux usages : un composant React (sous-titres, questions) et des chaînes
// HTML pour les mises à jour impératives de la boucle d'affichage (texte
// changé par `label.set`, formule transformée). Le texte hors formule est
// échappé : jamais de HTML venu du document.

import { Fragment, useMemo, type ReactNode } from 'react';
import katex from 'katex';
import { splitMathText } from '@jeraisonne/db/cards';
import { JRAF_KATEX_OPTIONS } from '@jeraisonne/animation';

const cache = new Map<string, string | null>();

/** HTML KaTeX d'une formule, ou `null` si elle ne se rend pas. */
export function texToHtml(tex: string): string | null {
  const hit = cache.get(tex);
  if (hit !== undefined) return hit;
  let html: string | null;
  try {
    html = katex.renderToString(tex, {
      ...JRAF_KATEX_OPTIONS,
      throwOnError: true,
      displayMode: false,
      output: 'html',
    });
  } catch {
    html = null;
  }
  if (cache.size > 400) cache.clear();
  cache.set(tex, html);
  return html;
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** HTML d'un texte avec formules `$…$` (une formule invalide reste en source). */
export function mathTextToHtml(text: string): string {
  return splitMathText(text)
    .map((part) => {
      if (part.kind === 'text') return escapeHtml(part.value);
      return texToHtml(part.value) ?? escapeHtml(part.value);
    })
    .join('');
}

/** HTML d'une formule seule (sans `$`) ; la source échappée si elle ne se rend pas. */
export function formulaToHtml(tex: string): string {
  return texToHtml(tex) ?? escapeHtml(tex);
}

// Typographie française des textes affichés (sous-titres, questions) : une
// espace insécable avant « ? ! : ; » et à l'intérieur des guillemets, pour
// qu'un signe ne parte jamais seul en début de ligne. Même largeur qu'une
// espace : la mise en page ne bouge pas.
const NBSP = '\u00a0';
const BEFORE_HIGH_PUNCT = /[ \t\u00a0\u202f]+(?=[?!:;»])/g;
const AFTER_OPEN_QUOTE = /«[ \t\u00a0\u202f]+/g;

/** Groupes de chiffres d'un nombre (« 150 000 000 ») : ils restent sur la même ligne. */
const DIGIT_GROUP = /(\d)[ \u202f](?=\d{3}(?!\d))/g;
/** Une unité après un nombre (« % », « € », « h », « V », « N/kg », « al »…), pas le début d'un mot. */
const UNIT = String.raw`(?:[%€‰]|°[CF]?|(?:k|m|c|M|G)?(?:Wh|W|J|V|A|N|Hz|g|m|L|s)|h|min|t|al)(?:\/[a-zA-Z]+)?(?![\p{L}\d])`;
/** Un nombre et son unité (« 20 % », « 33 € », « 2 h », « 230 V », « 9,8 N/kg ») : même ligne. */
const NUMBER_UNIT = new RegExp(String.raw`(\d)[ \u202f](?=${UNIT})`, 'gu');
/** L'unité qui suit une formule terminée par un nombre (« $T = 2$ ms ») : même ligne aussi. */
const UNIT_AFTER_FORMULA = new RegExp(String.raw`^[ \u202f](?=${UNIT})`, 'u');
const ENDS_WITH_NUMBER = /\d\}*\s*$/;

/** Espaces insécables de la typographie française (hors formules). */
export function frenchSpacing(text: string): string {
  return text
    .replace(BEFORE_HIGH_PUNCT, NBSP)
    .replace(AFTER_OPEN_QUOTE, `«${NBSP}`)
    .replace(DIGIT_GROUP, `$1${NBSP}`)
    .replace(NUMBER_UNIT, `$1${NBSP}`);
}

/** Ponctuation (« . », « , », « ?» insécable…) ou unité collée après une formule : elle la suit sur sa ligne. */
const GLUED_AFTER_MATH = /^[^ \t\n\r]+/;

/** Texte avec formules `$…$`, rendu avec les options des animations. */
export function AnimationMathText({
  children,
  className,
}: {
  children: string;
  className?: string;
}) {
  const parts = useMemo(() => splitMathText(children ?? ''), [children]);
  const content: ReactNode[] = [];
  // le texte qui suit chaque formule, sans la ponctuation déjà collée à elle
  let carry: string | null = null;
  parts.forEach((part, i) => {
    if (part.kind === 'text') {
      const text = carry ?? frenchSpacing(part.value);
      carry = null;
      if (text) content.push(<Fragment key={i}>{text}</Fragment>);
      return;
    }
    const html = texToHtml(part.value);
    const formula =
      html === null ? (
        <Fragment key={`f${i}`}>{part.value}</Fragment>
      ) : (
        <span key={`f${i}`} className="jr-math-text" dangerouslySetInnerHTML={{ __html: html }} />
      );
    const next = parts[i + 1];
    let after = next?.kind === 'text' ? frenchSpacing(next.value) : '';
    if (ENDS_WITH_NUMBER.test(part.value)) after = after.replace(UNIT_AFTER_FORMULA, NBSP);
    const glued = after.match(GLUED_AFTER_MATH)?.[0] ?? '';
    if (!glued) {
      content.push(formula);
      return;
    }
    // une formule et le signe qui la suit ne se séparent pas en fin de ligne
    content.push(
      <span key={i} className="whitespace-nowrap">
        {formula}
        {glued}
      </span>,
    );
    carry = after.slice(glued.length);
  });
  return className ? <span className={className}>{content}</span> : <>{content}</>;
}
